/**
 * Basecamp Projects - cache sync status prototype
 *
 * Access: /?test=basecamp-projects-cache
 * Chrome SoT: /?test=maater-ui#list-status-chrome (also #wait-sync, #menu-chrome)
 * Product: org-shared cache + permission-gated reads
 *   docs/product/bets/2026-08-17-org-wide-overdue-query-and-todos-assignee-facet.md
 *
 * Scope lock (this view):
 * - Project *list* = caller's Basecamp OAuth membership (same as Basecamp web).
 * - Cache depth column = org-shared Postgres for projects you can see.
 * - Refresh = manual pull (extension Rule 5); never auto-seed on Connect.
 *
 * Chrome lock (document-viewer continuity):
 * - No teaching banner above the list - (i) tooltip on title + status bar.
 * - Sync status lives on the bottom status bar, not a top strip.
 * - Reload list / Refresh visible live in view overflow `...` (not toolbar buttons).
 */

import { useCallback, useMemo, useState, type MouseEvent } from 'react'
import {
  AppWindow,
  Bookmark,
  Check,
  CheckSquare,
  Eye,
  FolderOpen,
  Globe,
  Inbox,
  Info,
  Loader2,
  Minus,
  MoreHorizontal,
  RefreshCw,
} from 'lucide-react'
import { useTheme } from '@/providers/theme-provider'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  EntityContextMenu,
  type EntityContextMenuEntry,
} from '@/components/features/entity-context-menu'
import { PreviewPanelToggle } from '@/components/integrations/_shared/preview-panel-toggle'
import {
  ProtoAppHeader,
  ProtoIconRail,
  ProtoModePanel,
  ProtoTabRow,
  ProtoTitleRow,
} from '@/components/prototype/maater-proto-chrome'
import {
  MaaterProtoSegmentTrack,
  maaterProtoAppSkinStyle,
} from '@/components/prototype/maater-proto-shell'
import { QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS } from '@/lib/menu-chrome'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'

type CacheDepth = 'ok' | 'partial' | 'none'
type ViewFrame = 'idle' | 'refreshing-visible' | 'refreshing-row' | 'done'

type ProtoProject = {
  id: string
  name: string
  cache: CacheDepth
  todoListsCached: number | null
  todosCached: number | null
  cacheUpdatedAt: string | null
}

const MEMBERSHIP_HELP =
  'Your Basecamp membership. This list only includes projects your OAuth user can see. Cache depth is org-shared for those projects - not a full-account dump for everyone.'

const INITIAL_PROJECTS: ProtoProject[] = [
  {
    id: '1',
    name: 'GGSC',
    cache: 'ok',
    todoListsCached: 12,
    todosCached: 184,
    cacheUpdatedAt: '2026-08-17T18:02:00Z',
  },
  {
    id: '2',
    name: 'Noozhawk',
    cache: 'partial',
    todoListsCached: 3,
    todosCached: 22,
    cacheUpdatedAt: '2026-08-10T11:00:00Z',
  },
  {
    id: '3',
    name: 'Hop Studios Internal',
    cache: 'ok',
    todoListsCached: 8,
    todosCached: 96,
    cacheUpdatedAt: '2026-08-17T20:10:00Z',
  },
  {
    id: '4',
    name: 'Client Alpha (wide)',
    cache: 'none',
    todoListsCached: null,
    todosCached: null,
    cacheUpdatedAt: null,
  },
  {
    id: '5',
    name: 'Archive - Old Site',
    cache: 'partial',
    todoListsCached: 1,
    todosCached: 4,
    cacheUpdatedAt: '2026-06-01T09:00:00Z',
  },
]

function formatRelative(iso: string | null): string {
  if (!iso) return 'Never'
  const diff = Date.now() - new Date(iso).getTime()
  const h = Math.floor(diff / 3_600_000)
  if (h < 1) return 'Just now'
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

/** ADR-0020: right-click and overflow `...` share one item source. */
function buildProjectRowMenuEntries(opts: {
  busy: boolean
  onPreview: () => void
  onOpen: () => void
  onRefresh: () => void
  onOpenExternal: () => void
}): EntityContextMenuEntry[] {
  const { busy, onPreview, onOpen, onRefresh, onOpenExternal } = opts
  return [
    { label: 'Preview', icon: Eye, onSelect: onPreview },
    { label: 'Open', icon: AppWindow, onSelect: onOpen },
    {
      label: 'Refresh cache',
      icon: RefreshCw,
      disabled: busy,
      onSelect: onRefresh,
    },
    { separator: true },
    {
      label: 'Open in Basecamp',
      icon: Globe,
      onSelect: onOpenExternal,
    },
  ]
}

function CacheStatusCell({
  depth,
  busy,
}: {
  depth: CacheDepth
  busy?: boolean
}) {
  if (busy) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        Refreshing
      </span>
    )
  }
  if (depth === 'ok') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-500">
        <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
        Ok
      </span>
    )
  }
  if (depth === 'partial') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-500">
        <Minus className="h-3.5 w-3.5" />
        Partial
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <span className="inline-block h-2 w-2 rounded-full border border-muted-foreground/50" />
      None
    </span>
  )
}

function statusLine(frame: ViewFrame, rowName?: string | null): string {
  if (frame === 'refreshing-visible') return 'Refreshing cache for visible projects…'
  if (frame === 'refreshing-row') return `Refreshing ${rowName ?? 'project'}…`
  if (frame === 'done') return 'Cache updated · just now'
  return 'Idle · pull on demand'
}

/** Document-viewer pattern: quiet bottom bar; teach via (i), not a banner. */
function ListStatusBar({
  frame,
  rowName,
  visibleCount,
  helpText,
}: {
  frame: ViewFrame
  rowName?: string | null
  visibleCount: number
  helpText: string
}) {
  const working = frame === 'refreshing-visible' || frame === 'refreshing-row'
  return (
    <div
      className="flex h-8 shrink-0 items-center gap-2 border-t border-border bg-muted/20 px-3 text-xs text-muted-foreground"
      data-testid={testIds.basecampProjectsCache.statusBar}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {working ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
        ) : null}
        <span className="truncate" data-testid={testIds.basecampProjectsCache.statusText}>
          {statusLine(frame, rowName)}
        </span>
        <span className="text-muted-foreground/50" aria-hidden>
          ·
        </span>
        <span className="shrink-0 tabular-nums">{visibleCount} shown</span>
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
            aria-label="About this list"
            data-testid={testIds.basecampProjectsCache.statusInfo}
          >
            <Info className="h-3.5 w-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="max-w-xs text-xs leading-relaxed">
          {helpText}
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export function TestBasecampProjectsCache() {
  const { theme, setTheme } = useTheme()
  const [projects, setProjects] = useState<ProtoProject[]>(INITIAL_PROJECTS)
  const [frame, setFrame] = useState<ViewFrame>('idle')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>('2')
  const [previewOpen, setPreviewOpen] = useState(true)
  const [filter, setFilter] = useState<'all' | 'ok' | 'partial' | 'none'>('all')
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    project: ProtoProject
  } | null>(null)

  const busy = frame === 'refreshing-visible' || frame === 'refreshing-row'

  const selected = useMemo(
    () => projects.find((p) => p.id === selectedId) ?? null,
    [projects, selectedId],
  )

  const visible = useMemo(() => {
    if (filter === 'all') return projects
    return projects.filter((p) => p.cache === filter)
  }, [projects, filter])

  const counts = useMemo(() => {
    const c = { ok: 0, partial: 0, none: 0 }
    for (const p of projects) c[p.cache] += 1
    return c
  }, [projects])

  const finishRefresh = useCallback((ids: string[]) => {
    const iso = new Date().toISOString()
    setProjects((prev) =>
      prev.map((p) => {
        if (!ids.includes(p.id)) return p
        return {
          ...p,
          cache: 'ok',
          todoListsCached: p.todoListsCached ?? 4,
          todosCached: Math.max(p.todosCached ?? 0, 18),
          cacheUpdatedAt: iso,
        }
      }),
    )
    setBusyId(null)
    setFrame('done')
    window.setTimeout(() => setFrame('idle'), 1800)
  }, [])

  const refreshVisible = useCallback(() => {
    if (busy) return
    setFrame('refreshing-visible')
    const ids = visible.map((p) => p.id)
    window.setTimeout(() => finishRefresh(ids), 1400)
  }, [busy, visible, finishRefresh])

  const reloadList = useCallback(() => {
    if (busy) return
    setFrame('refreshing-visible')
    window.setTimeout(() => {
      setFrame('done')
      window.setTimeout(() => setFrame('idle'), 1800)
    }, 700)
  }, [busy])

  const refreshRow = useCallback(
    (id: string) => {
      if (busy) return
      setSelectedId(id)
      setBusyId(id)
      setFrame('refreshing-row')
      window.setTimeout(() => finishRefresh([id]), 1100)
    },
    [busy, finishRefresh],
  )

  const openPreviewFor = useCallback((project: ProtoProject) => {
    setSelectedId(project.id)
    setPreviewOpen(true)
  }, [])

  const openProjectStub = useCallback((project: ProtoProject) => {
    setSelectedId(project.id)
  }, [])

  const openExternalStub = useCallback((_project: ProtoProject) => {
    /* prototype stand-in */
  }, [])

  const menuEntriesFor = useCallback(
    (project: ProtoProject) =>
      buildProjectRowMenuEntries({
        busy,
        onPreview: () => openPreviewFor(project),
        onOpen: () => openProjectStub(project),
        onRefresh: () => refreshRow(project.id),
        onOpenExternal: () => openExternalStub(project),
      }),
    [busy, openPreviewFor, openProjectStub, openExternalStub, refreshRow],
  )

  const handleRowContextMenu = useCallback((project: ProtoProject, e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedId(project.id)
    setContextMenu({ x: e.clientX, y: e.clientY, project })
  }, [])

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="flex h-screen min-h-screen flex-col bg-background text-foreground antialiased"
        style={maaterProtoAppSkinStyle()}
        data-maater-gallery-skin="app"
        data-testid={testIds.basecampProjectsCache.root}
      >
        <ProtoAppHeader
          themeControl="light-dark"
          appTheme={(theme as 'light' | 'dark' | 'system') || 'system'}
          onAppTheme={(t) => {
            void setTheme(t)
          }}
          badge="bc-projects-cache"
          testId={testIds.basecampProjectsCache.appHeader}
          prototypeIndexTestId={testIds.basecampProjectsCache.backToIndex}
        />

        <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border px-3">
          <span className="text-[10px] text-muted-foreground">
            Continuity harness · chrome SoT ={' '}
            <a href="/?test=maater-ui#list-status-chrome" className="underline">
              /?test=maater-ui#list-status-chrome
            </a>
          </span>
          <MaaterProtoSegmentTrack
            value={frame === 'idle' || frame === 'done' ? 'idle' : frame}
            onChange={(id) => {
              if (id === 'idle') setFrame('idle')
              if (id === 'refreshing-visible') {
                setFrame('refreshing-visible')
                window.setTimeout(() => setFrame('done'), 800)
              }
              if (id === 'refreshing-row') {
                setBusyId(selectedId)
                setFrame('refreshing-row')
                window.setTimeout(() => {
                  setBusyId(null)
                  setFrame('done')
                }, 800)
              }
            }}
            options={[
              { id: 'idle', label: 'Idle' },
              { id: 'refreshing-visible', label: 'View refresh' },
              { id: 'refreshing-row', label: 'Row refresh' },
            ]}
          />
        </div>

        <div className="flex min-h-0 flex-1">
          <ProtoIconRail active="documents" testId={testIds.basecampProjectsCache.iconRail} />
          <ProtoModePanel
            title="Basecamp"
            subtitle="Extension limb"
            testId={testIds.basecampProjectsCache.modePanel}
          >
            <nav className="flex flex-col gap-0.5 p-2 text-sm">
              {[
                { id: 'bookmarks', label: 'Bookmarks', icon: Bookmark },
                { id: 'mystuff', label: 'My Stuff', icon: Inbox },
                { id: 'todos', label: 'Todos', icon: CheckSquare },
                { id: 'projects', label: 'Projects', icon: FolderOpen, active: true },
              ].map((item) => {
                const Icon = item.icon
                return (
                  <div
                    key={item.id}
                    className={cn(
                      'flex h-9 items-center gap-2 rounded-md px-2',
                      item.active ? 'bg-muted font-medium' : 'text-muted-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {item.label}
                  </div>
                )
              })}
            </nav>
          </ProtoModePanel>

          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <ProtoTabRow
              tabs={[{ id: 'projects', label: 'Projects' }]}
              activeId="projects"
              onSelect={() => {}}
              testId={testIds.basecampProjectsCache.tabRow}
            />
            <ProtoTitleRow
              crumbs={[
                { id: 'bc', label: 'Basecamp' },
                { id: 'projects', label: 'Projects' },
              ]}
              title="Projects"
              density="inline"
              actions={
                <div className="flex items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-muted-foreground"
                        aria-label="About this list"
                        data-testid={testIds.basecampProjectsCache.titleInfo}
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="end"
                      className="max-w-xs text-xs leading-relaxed"
                    >
                      {MEMBERSHIP_HELP}
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-muted-foreground"
                        aria-label="More actions"
                        data-testid={testIds.basecampProjectsCache.viewOverflow}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
                    >
                      <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Cache
                      </DropdownMenuLabel>
                      <DropdownMenuItem
                        disabled={busy}
                        onClick={reloadList}
                        data-testid={testIds.basecampProjectsCache.reloadList}
                      >
                        <RefreshCw className="mr-2 h-3.5 w-3.5" />
                        Reload list
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={busy}
                        onClick={refreshVisible}
                        data-testid={testIds.basecampProjectsCache.refreshVisible}
                      >
                        <RefreshCw className="mr-2 h-3.5 w-3.5" />
                        Refresh visible
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <PreviewPanelToggle
                    open={previewOpen}
                    onToggle={() => setPreviewOpen((v) => !v)}
                    testId={testIds.basecampProjectsCache.previewToggle}
                  />
                </div>
              }
              testId={testIds.basecampProjectsCache.titleRow}
            />

            <div className="flex min-h-0 flex-1">
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
                  <MaaterProtoSegmentTrack
                    value={filter}
                    onChange={(id) => setFilter(id as typeof filter)}
                    options={[
                      { id: 'all', label: `All (${projects.length})` },
                      { id: 'ok', label: `Ok (${counts.ok})` },
                      { id: 'partial', label: `Partial (${counts.partial})` },
                      { id: 'none', label: `None (${counts.none})` },
                    ]}
                  />
                </div>

                <div
                  className="min-h-0 flex-1 overflow-auto"
                  data-testid={testIds.basecampProjectsCache.table}
                >
                  <table className="w-full table-fixed border-collapse text-sm">
                    <thead className="sticky top-0 z-10 bg-background">
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="px-4 py-2 font-medium">Project</th>
                        <th className="w-[100px] px-2 py-2 font-medium">Cache</th>
                        <th className="w-[72px] px-2 py-2 font-medium">Lists</th>
                        <th className="w-[72px] px-2 py-2 font-medium">Todos</th>
                        <th className="w-[100px] px-2 py-2 font-medium">Updated</th>
                        <th className="w-[88px] px-2 py-2 font-medium" />
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((p) => {
                        const isSelected = p.id === selectedId
                        const rowBusy = busyId === p.id && frame === 'refreshing-row'
                        const entries = menuEntriesFor(p)
                        return (
                          <tr
                            key={p.id}
                            className={cn(
                              'group border-b border-border/60 hover:bg-muted/40',
                              isSelected && 'bg-muted/60',
                            )}
                            data-testid={testIds.basecampProjectsCache.row(p.id)}
                            onClick={() => setSelectedId(p.id)}
                            onContextMenu={(e) => handleRowContextMenu(p, e)}
                          >
                            <td className="px-4 py-2.5">
                              <div className="flex min-w-0 items-center gap-2">
                                <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <span className="truncate font-medium">{p.name}</span>
                              </div>
                            </td>
                            <td className="px-2 py-2.5">
                              <CacheStatusCell depth={p.cache} busy={rowBusy} />
                            </td>
                            <td className="px-2 py-2.5 tabular-nums text-muted-foreground">
                              {p.todoListsCached ?? '—'}
                            </td>
                            <td className="px-2 py-2.5 tabular-nums text-muted-foreground">
                              {p.todosCached ?? '—'}
                            </td>
                            <td className="px-2 py-2.5 text-xs text-muted-foreground">
                              {formatRelative(p.cacheUpdatedAt)}
                            </td>
                            <td className="px-2 py-2.5">
                              <div className="flex items-center justify-end gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  title="Refresh this project into org cache"
                                  disabled={busy}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    refreshRow(p.id)
                                  }}
                                  data-testid={testIds.basecampProjectsCache.rowRefresh(p.id)}
                                >
                                  <RefreshCw
                                    className={cn('h-3.5 w-3.5', rowBusy && 'animate-spin')}
                                  />
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <MoreHorizontal className="h-3.5 w-3.5" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="end"
                                    className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
                                  >
                                    {entries.map((entry, i) => {
                                      if ('separator' in entry && entry.separator) {
                                        return <DropdownMenuSeparator key={`sep-${i}`} />
                                      }
                                      if (!('label' in entry) || !('onSelect' in entry)) {
                                        return null
                                      }
                                      return (
                                        <DropdownMenuItem
                                          key={entry.label}
                                          disabled={Boolean(entry.disabled)}
                                          onClick={() => entry.onSelect()}
                                        >
                                          {entry.label}
                                        </DropdownMenuItem>
                                      )
                                    })}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                <ListStatusBar
                  frame={frame}
                  rowName={busyId ? projects.find((p) => p.id === busyId)?.name : null}
                  visibleCount={visible.length}
                  helpText={MEMBERSHIP_HELP}
                />
              </div>

              {previewOpen ? (
                <aside
                  className="flex w-[320px] shrink-0 flex-col border-l border-border bg-muted/10"
                  data-testid={testIds.basecampProjectsCache.previewPane}
                >
                  {selected ? (
                    <div className="space-y-4 p-4">
                      <div>
                        <h2 className="text-base font-semibold tracking-tight">
                          {selected.name}
                        </h2>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Cache vs live (caller OAuth)
                        </p>
                      </div>
                      <div className="space-y-2 rounded-md border border-border p-3 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Depth</span>
                          <CacheStatusCell depth={selected.cache} />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Todo lists</span>
                          <span className="tabular-nums">
                            {selected.todoListsCached ?? '—'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Todos cached</span>
                          <span className="tabular-nums">{selected.todosCached ?? '—'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Last cache write</span>
                          <span>{formatRelative(selected.cacheUpdatedAt)}</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="w-full gap-1.5"
                        disabled={busy}
                        onClick={() => refreshRow(selected.id)}
                        data-testid={testIds.basecampProjectsCache.previewRefresh}
                      >
                        <RefreshCw className={cn('h-3.5 w-3.5', busy && 'animate-spin')} />
                        Refresh this project
                      </Button>
                      <p className="text-[11px] leading-relaxed text-muted-foreground">
                        Full-account seed stays an operator CLI (run-as). This UI only pulls for
                        projects already in your membership.
                      </p>
                      <Badge variant="outline" className="text-[10px] font-normal">
                        Prototype · mock data
                      </Badge>
                    </div>
                  ) : (
                    <p className="p-4 text-sm text-muted-foreground">
                      Select a project, or choose Preview from the row menu.
                    </p>
                  )}
                </aside>
              ) : null}
            </div>
          </main>
        </div>

        {contextMenu ? (
          <EntityContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            data-testid={testIds.basecampProjectsCache.rowContextMenu}
            onClose={() => setContextMenu(null)}
            entries={menuEntriesFor(contextMenu.project)}
          />
        ) : null}
      </div>
    </TooltipProvider>
  )
}
